/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    Type,
    ViewChild,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { optionsPositionsPriority } from 'standalone-components/avi-dropdown';
import { DialogService } from 'ng/modules/core/services/dialog.service';

import {
    VsLogsSearchBarDropdownComponent,
} from './vs-logs-search-bar-dropdown/vs-logs-search-bar-dropdown.component';

import { VsLogsStore } from '../../../services/vs-logs.store';
import { VsLogsSearchBarStore } from './vs-logs-search-bar.store';
import {
    VsLogsSearchBarDropdownService,
} from '../../../services/vs-logs-search-bar-dropdown.service';
import {
    SaveSearchType,
    VsLogsSaveSearchModalComponent,
} from './vs-logs-save-search-modal/vs-logs-save-search-modal.component';

import { VsLogsFilterInputComponent } from './vs-logs-filter-input/vs-logs-filter-input.component';

import * as l10n from './vs-logs-search-bar.l10n';
import './vs-logs-search-bar.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const VS_LOGS_SAVE_SEARCH_MODAL_ID = 'VS_LOGS_SAVE_SEARCH_MODAL';

/**
 * @description VS Logs search bar component.
 * @author Alex Klyuev
 */
@Component({
    selector: 'vs-logs-search-bar',
    templateUrl: './vs-logs-search-bar.component.html',
    providers: [VsLogsSearchBarStore],
})
export class VsLogsSearchBarComponent implements AfterViewInit, OnDestroy {
    /**
     * Element ref for the search bar container.
     */
    @ViewChild('searchBarContainer')
    public searchBarContainerRef: ElementRef;

    /**
     * Element ref for the search icon.
     */
    @ViewChild('searchIcon')
    public searchIconRef: ElementRef;

    @ViewChild(VsLogsSearchBarDropdownComponent)
    private vsLogsSearchBarDropdown: VsLogsSearchBarDropdownComponent;

    @ViewChild(VsLogsFilterInputComponent)
    private vsLogsFilterInput: VsLogsFilterInputComponent;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /**
     * Save enum as class property for access in view.
     */
    public readonly SaveSearchTypeEnum = SaveSearchType;

    /**
     * Position definition of the dropdown relative to the search bar.
     */
    public readonly dropdownPosition: ConnectedPosition[] = optionsPositionsPriority;

    /**
     * Calculated width of the dropdown.
     */
    public dropdownWidth: number;

    /**
     * Observer of search bar to report changes in size.
     */
    private searchBarResizeObserver: ResizeObserver;

    constructor(
        l10nService: L10nService,
        public readonly vsLogsStore: VsLogsStore,
        public readonly vsLogsSearchBarStore: VsLogsSearchBarStore,
        public readonly vsLogsSearchBarDropdownService: VsLogsSearchBarDropdownService,
        private readonly dialogService: DialogService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngAfterViewInit(): void {
        // init search bar resize observer & callback
        this.searchBarResizeObserver = new ResizeObserver(this.setDropdownWidth);
        this.searchBarResizeObserver.observe(document.querySelector('.vs-logs-search-bar'));
    }

    /** @override */
    public ngOnDestroy(): void {
        this.searchBarResizeObserver.disconnect();
    }

    /**
     * Calculate and set width of dropdown.
     */
    public setDropdownWidth = (): void => {
        /* eslint-disable no-unsafe-optional-chaining */
        this.dropdownWidth = this.searchBarContainerRef?.nativeElement.offsetWidth -
            this.searchIconRef?.nativeElement.offsetWidth;
        /* eslint-enable no-unsafe-optional-chaining */
    };

    /**
     * Callback to ngFor-track by.
     */
    // eslint-disable-next-line class-methods-use-this
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Open the save search modal.
     */
    public openSaveSearchModal(type: SaveSearchType): void {
        this.dialogService.add({
            id: VS_LOGS_SAVE_SEARCH_MODAL_ID,
            component: VsLogsSaveSearchModalComponent as Type<Component>,
            componentProps: {
                saveSearchType: type,
                onClose: () => this.dialogService.remove(VS_LOGS_SAVE_SEARCH_MODAL_ID),
            },
        });
    }

    /**
     * Handle keydown event from search input. Used for adding keyboard controls to the dropdown.
     */
    public handleSearchKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'ArrowDown':
                this.vsLogsSearchBarDropdown.focusDropdownOptions();
                break;
            case 'Enter':
            case 'Tab':
                this.vsLogsSearchBarStore.addFilter();
                this.vsLogsSearchBarDropdownService.openDropdown();
                break;
        }
    }

    /**
     * Handle escaping dropdown options. Used for adding keyboard controls to the dropdown.
     */
    public handleEscapeDropdownOptions(): void {
        this.vsLogsFilterInput.refocus();
    }
}
